/**
 * Cyphex Teams — shared primitives.
 *
 * Everything in @cyphex/types is a WIRE contract: it describes JSON that
 * crosses a process boundary (REST body, WebSocket frame). It is NOT a
 * database row type — SQL columns are snake_case, the wire is camelCase.
 */

/** ISO-8601 UTC timestamp, always with a `Z` suffix. e.g. `2026-07-31T10:30:00.000Z` */
export type IsoDateTime = string;

/** ISO-8601 calendar date, no time component. e.g. `2026-07-31` */
export type IsoDate = string;

/** 24h local time, `HH:mm`. */
export type TimeOfDay = string;

/** IANA timezone identifier. e.g. `Asia/Kolkata` */
export type TimeZone = string;

/** RFC 5545 RRULE string, without the `RRULE:` prefix. */
export type RRule = string;

/** JSON value. Use instead of `any` for opaque payloads. */
export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export type JsonObject = { [key: string]: Json };

/**
 * Branded UUIDs. These are compile-time only — at runtime every one is a
 * plain lowercase UUID string. They stop a channelId being passed where a
 * teamId is expected, which is the single most common bug in this domain.
 */
declare const brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [brand]: B };

export type Uuid = string;
export type TenantId = Brand<Uuid, 'TenantId'>;
export type UserId = Brand<Uuid, 'UserId'>;
export type TeamId = Brand<Uuid, 'TeamId'>;
export type ChannelId = Brand<Uuid, 'ChannelId'>;
export type ConversationId = Brand<Uuid, 'ConversationId'>;
export type MessageId = Brand<Uuid, 'MessageId'>;
export type MeetingId = Brand<Uuid, 'MeetingId'>;
export type CallId = Brand<Uuid, 'CallId'>;
export type FileId = Brand<Uuid, 'FileId'>;
export type FolderId = Brand<Uuid, 'FolderId'>;
export type CalendarId = Brand<Uuid, 'CalendarId'>;
export type EventId = Brand<Uuid, 'EventId'>;
export type NotificationId = Brand<Uuid, 'NotificationId'>;
export type PollId = Brand<Uuid, 'PollId'>;
export type TaskId = Brand<Uuid, 'TaskId'>;
export type ApprovalId = Brand<Uuid, 'ApprovalId'>;
export type DeviceId = Brand<Uuid, 'DeviceId'>;
export type AppId = Brand<Uuid, 'AppId'>;
export type RoleId = Brand<Uuid, 'RoleId'>;
export type GroupId = Brand<Uuid, 'GroupId'>;

/** Cast helper for the boundary where a raw string becomes a branded id. */
export const asId = <T extends Uuid>(value: string): T => value as T;

/** Per-conversation gapless ordinal. The ONLY ordering key for messages. */
export type Seq = number;

/**
 * Where something lives. Mirrors the `cyphex.scope_kind` enum.
 */
export type ScopeKind =
  | 'tenant'
  | 'team'
  | 'channel'
  | 'conversation'
  | 'meeting'
  | 'call'
  | 'user';

export interface Scope {
  kind: ScopeKind;
  id: Uuid | null;
}

export type Visibility = 'public' | 'private' | 'hidden';

/**
 * Avatar / team-tile tint. A SEMANTIC TOKEN, never a hex value — the UI maps
 * it to a CSS variable so light/dark and tenant branding both work. The
 * database stores this string verbatim in `avatar_color` columns.
 *
 * A RUNTIME TUPLE, not a bare union, because the union alone could not be
 * enforced: every write route validated `/^#[0-9a-fA-F]{6}$/` against a column
 * whose default is `'orange'` and whose every reader casts to this type, so the
 * contract rejected all eleven legal values and accepted only illegal ones.
 * `POST /teams` went further and wrote a literal `'#F97316'`, which is why all
 * eleven teams in the live tenant hold a hex string in a token column. The
 * damage stayed invisible because the client narrows with a type guard and
 * falls back to a name-derived colour, so the tile still drew - in a colour
 * nobody picked. Route schemas build their enum from this tuple now.
 */
export const AVATAR_COLORS = [
  'teal',
  'pink',
  'blue',
  'orange',
  'green',
  'indigo',
  'violet',
  'rose',
  'amber',
  'cyan',
  'slate',
] as const;

export type AvatarColor = (typeof AVATAR_COLORS)[number];

/**
 * The assignment cycle, DELIBERATELY the palette minus `slate`: slate is the
 * neutral a caller may choose and the guard's last resort, never a colour
 * something is handed by luck.
 */
export const AVATAR_COLOR_CYCLE: readonly AvatarColor[] = [
  'teal', 'pink', 'blue', 'orange', 'green', 'indigo', 'violet', 'rose', 'amber', 'cyan',
];

/**
 * Deterministic colour for a name/id, so the same team is the same colour on
 * every device and in both trees.
 *
 * Lives here rather than in `@cyphex/ui` because the API needs it too and
 * cannot import a browser package. A team created without an explicit colour is
 * stamped with this - which is precisely what the client used to compute for
 * itself after rejecting the stored hex. Two implementations of one hash would
 * mean the server and the browser disagreeing about what colour a team is, so
 * `@cyphex/ui`'s `teamColorFor` delegates here rather than repeating it.
 */
export function avatarColorFor(seed: string): AvatarColor {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLOR_CYCLE[hash % AVATAR_COLOR_CYCLE.length] ?? 'slate';
}

export type MemberRole = 'owner' | 'moderator' | 'member' | 'guest';

export type MembershipState =
  | 'active'
  | 'invited'
  | 'requested'
  | 'left'
  | 'removed'
  | 'banned';

export type NotificationLevel = 'all' | 'mentions' | 'none';

/** Every entity that is soft-deletable exposes these. */
export interface Timestamps {
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt?: IsoDateTime | null;
}

/* ---------------------------------------------------------------------------
 * Pagination
 * ------------------------------------------------------------------------- */

/**
 * Opaque, server-generated cursor. Base64url of an internal keyset tuple.
 * Clients MUST treat it as an opaque token and never construct or parse one.
 */
export type Cursor = string;

export interface CursorPageRequest {
  /** Max items. Server clamps to the resource's documented maximum. */
  limit?: number;
  /** Fetch the page after this cursor. */
  after?: Cursor;
  /** Fetch the page before this cursor (backwards scroll in a timeline). */
  before?: Cursor;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: Cursor | null;
  endCursor: Cursor | null;
  /** Only present on resources where an exact count is cheap. */
  totalCount?: number;
}

export interface Page<T> {
  items: T[];
  pageInfo: PageInfo;
}

/* ---------------------------------------------------------------------------
 * Error envelope — EVERY non-2xx REST response and every `error` WS frame
 * uses exactly this shape. No exceptions.
 * ------------------------------------------------------------------------- */

export type ErrorCode =
  // 400
  | 'bad_request'
  | 'validation_failed'
  | 'unsupported_media_type'
  // 401 / 403
  | 'unauthenticated'
  | 'token_expired'
  | 'mfa_required'
  | 'forbidden'
  | 'tenant_mismatch'
  | 'guest_not_allowed'
  // 404 / 409 / 410
  | 'not_found'
  | 'conflict'
  | 'already_exists'
  | 'gone'
  | 'precondition_failed'
  // 413 / 422 / 429
  | 'payload_too_large'
  | 'quota_exceeded'
  | 'rate_limited'
  // 5xx
  | 'internal_error'
  | 'dependency_unavailable'
  | 'not_implemented';

export interface FieldError {
  /** JSON Pointer into the request body, e.g. `/body/title`. */
  path: string;
  code: string;
  message: string;
}

export interface ApiError {
  error: {
    code: ErrorCode;
    /** Human-readable, safe to log. Never surfaced raw to end users. */
    message: string;
    /** Present for `validation_failed`. */
    fields?: FieldError[];
    /** Echoes the `X-Request-Id` header. Always present. */
    requestId: string;
    /** Seconds to wait, present on `rate_limited` and `dependency_unavailable`. */
    retryAfter?: number;
    /** Machine-usable extras (quota limits, conflicting id, ...). */
    details?: JsonObject;
  };
}

/** Convenience discriminated result used by the generated API client. */
export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError['error'] };

/* ---------------------------------------------------------------------------
 * Idempotency
 * ------------------------------------------------------------------------- */

/**
 * Every unsafe (POST/PUT/PATCH/DELETE) request MAY carry `Idempotency-Key`.
 * Message sends MUST carry one (`clientMsgId` in the body doubles as it).
 * The server stores the first response for 24h and replays it verbatim.
 */
export interface IdempotentRequest {
  idempotencyKey?: Uuid;
}
